import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    List,
    Select,
    Grid,
    Box,
    ListItemText,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { Link } from 'react-router-dom';
import { planActions } from '../pages/PlanPage/store';
import { connect } from 'react-redux';
import DialogCloseButton from '../buttons/DialogCloseButton';
import AppConstants from '../../App/AppConstants';
import { appOperations } from '../../App/store';
import CircleProgressButton from '../buttons/CircleProgressButton';
import TooltipHelpButton from '../buttons/TooltipHelpButton';
import { trackEvent, AnalyticsConstants } from '../../utilities/analyticsUtilities';
import { getCurrencySymbol } from '../../utilities/stringUtilities';
import { collectionHelper } from '../../utilities/basicUtilities';
import { PurchaseOptionsMapping } from '../pages/PlanPage/PlanPageMappings';
import PlanPageConstants from '../pages/PlanPage/PlanPageConstants';

const styles = theme => ({
    dialogPaper: {
        width: 350
    },
    unstyledLink: {
        textDecoration: 'none'
    },
    quantityFormControl: {
        marginBottom: theme.spacing(2)
    }
});

class AddVideoCreditsDialog extends React.Component {
    state = {
        quantity: 1,
        planType: PlanPageConstants.PLAN_TYPES.PRO
    }

    render() {
        const { 
            classes, onClose, open, accountDetails, checkingOutId, updatingSubscriptionId, 
            purchaseOptions, recommendedCurrency 
        } = this.props;
        
        const { quantity, planType } = this.state;

        const isProPlanType = planType === PlanPageConstants.PLAN_TYPES.PRO;
        const creditsAmount = quantity + ( isProPlanType ? accountDetails.professionalCredits : accountDetails.fourKCredits);
        const isProSubscription = accountDetails.subscriptionType === AppConstants.PROFESSIONAL_SUBSCRIPTION_TYPE;

        // Determine which purchase options we need to use on this component

        const videoCreditOption = collectionHelper(purchaseOptions)
            .firstOrDefault((po) => po.id === (isProPlanType ?
                PurchaseOptionsMapping.getVideoCreditOptionId(recommendedCurrency) :
                PurchaseOptionsMapping.get4KVideoCreditOptionId(recommendedCurrency)));

        return (
            <Dialog
                open={open}
                onClose={onClose}
                aria-labelledby='video-credit-dialog-title'
                PaperProps={{
                    className: classes.dialogPaper
                }}
                TransitionProps={{
                    onEnter: this.onEnter
                }}>
                <DialogTitle id='video-credit-dialog-title'>
                    Add Video Credits
                    <DialogCloseButton
                        onClick={onClose}
                    />
                </DialogTitle>
                <DialogContent>
                    {isProSubscription &&
                        <>
                        <List>
                            <Grid
                                container
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Grid size={{ md: 10 }}>
                                    <ListItemText>
                                        <FormControl sx={{ minWidth: "60px" }} variant="standard" className={classes.quantityFormControl}>
                                            <InputLabel id='credit-quantity-label'>Quantity</InputLabel>
                                            <Select
                                                variant="standard"
                                                value={quantity}
                                                onChange={this.onQuantityChange}
                                                labelId='credit-quantity-label'>
                                                {this.renderMenuItems()}
                                            </Select>
                                        </FormControl>
                                        <br />
                                        <FormControl sx={{ minWidth: "60px" }} variant="standard">
                                            <InputLabel id='credit-plan-type-label'>Resolution</InputLabel>
                                            <Select
                                                variant="standard"
                                                value={planType}
                                                onChange={this.onPlanTypeChange}
                                                labelId='credit-plan-type-label'>
                                                <MenuItem
                                                    value={PlanPageConstants.PLAN_TYPES.PRO}
                                                >
                                                    1080p
                                                </MenuItem>
                                                <MenuItem
                                                    value={PlanPageConstants.PLAN_TYPES.FOURK}
                                                >
                                                    4K
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </ListItemText>
                                </Grid>
                                <Grid>
                                    <Box>
                                        <Typography
                                            variant='caption'
                                            color='textSecondary'
                                            component='p'
                                            align='right'
                                        >
                                            Total
                                        </Typography>
                                        <Typography
                                            variant='h6'
                                            component='p'
                                        >
                                            {videoCreditOption ? `${getCurrencySymbol(recommendedCurrency)}${videoCreditOption.amount * quantity}` : ''}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </List>
                            <br/>
                            <Typography 
                                variant='body2'
                            >
                                <>
                                    After purchase you will have {creditsAmount} video credits 
                                    and can export up to {10 * creditsAmount} minutes of video.
                                    <TooltipHelpButton 
                                        title={`
                                            Each video credit can be spent to export 1 video up to 10 minutes long.
                                            1080p credits can be combined to export longer videos up to 10 minutes / credit.
                                            Max video length is 60 minutes.
                                            4K credits cannot be combined.
                                        `}
                                    />
                                </>
                            </Typography>
                            <br/>
                        </>
                    }
                </DialogContent>
                <DialogActions>
                    <Link to='/plans' className={classes.unstyledLink}>
                        <Button
                            onClick={() => {
                                setTimeout(() => window.scrollTo(0, 0), 10)
                                onClose();
                            }}
                        >
                            More Options
                        </Button>
                    </Link>
                    <CircleProgressButton
                        variant='outlined'
                        color='primary'
                        disabled={!isProSubscription || Boolean(checkingOutId) || Boolean(updatingSubscriptionId)}
                        loading={Boolean(checkingOutId)}
                        onClick={() => this.onPurchaseClick(videoCreditOption)}
                    >
                        Purchase
                    </CircleProgressButton>
                </DialogActions>
            </Dialog>
        );
    }

    renderMenuItems = () => {
        const quantities = [1,2,3,4,5,6,7,8,9,10];

        return quantities.map((quantity, i) =>
            <MenuItem 
                value={quantity}
                key={i}
            >
                {quantity}
            </MenuItem>
        );
    }

    componentDidMount() {
        const { defaultQuantity, loadPurchaseOptions } = this.props;

        loadPurchaseOptions();

        if (defaultQuantity)
            this.setState({quantity: defaultQuantity});
    }

    onEnter = () => {
        const { location } = this.props;
        
        trackEvent('Opened Add Credits Dialog', {
            category: AnalyticsConstants.ACQUISITION_CATEGORY,
            location: location,
        })
    }

    onQuantityChange = (event) => this.setState({quantity: event.target.value});
    onPlanTypeChange = (event) => this.setState({planType: event.target.value});

    onPurchaseClick = (videoCreditOption) => {
        const { initiateCheckout, location } = this.props;
        const { quantity } = this.state;
        initiateCheckout(videoCreditOption.id, quantity, location);
    }
}

const mapStateToProps = (state) => {
    const { 
        accountDetails,
        purchaseOptions,
        recommendedCurrency
    } = state.app;

    const {
        checkingOutId,
        updatingSubscriptionId
    } = state.plan;

    return {
        accountDetails,
        purchaseOptions,
        recommendedCurrency,
        checkingOutId,
        updatingSubscriptionId
    }
}

const mapDispatchToProps = (dispatch) => {
    const loadPurchaseOptions = () => dispatch(appOperations.loadPurchaseOptions());
    const initiateCheckout = (optionId, quantity, location) => dispatch(planActions.initiateCheckout(optionId, quantity, location));

    return {
        initiateCheckout,
        loadPurchaseOptions
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(AddVideoCreditsDialog, styles));